// App viewport - full-screen overlay for content views (account, play, characters, etc.)

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

/**
 * Full-screen viewport overlay for app content views.
 * Renders a dark backdrop with a centered, blurred content panel.
 * @fires close - when backdrop or X button is clicked
 */
class AppViewport extends BaseComponent {
  static properties = {
    heading: { type: String },
    noPadding: { type: Boolean, attribute: 'no-padding' },
    backHref: { type: String, attribute: 'back-href' },
    backLabel: { type: String, attribute: 'back-label' },
  };

  static styles = css`
    :host {
      display: contents;
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.4);
      display: flex;
      align-items: stretch;
      justify-content: center;
      z-index: 1040;
      padding: 1.5rem 0;
    }

    .modal-panel {
      width: 576px;
      margin: 0 1.5rem;
      background: #ffffff;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    @media (min-width: 768px) {
      .modal-panel {
        width: 768px;
      }
    }
    @media (min-width: 992px) {
      .modal-panel {
        width: 992px;
      }
    }
    @media (min-width: 1200px) {
      .modal-panel {
        width: 1200px;
      }
    }
    @media (min-width: 1400px) {
      .modal-panel {
        width: 1400px;
      }
    }

    .modal-header {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      align-items: center;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid #dee2e6;
    }

    .header-left {
      justify-self: start;
    }

    .header-right {
      justify-self: end;
    }

    .modal-title {
      margin: 0;
      font-size: 1.75rem;
      font-weight: 700;
      color: #212529;
      font-family: var(--app-viewport-heading-font, 'MedievalSharp', Georgia, serif);
      letter-spacing: 0.12em;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
    /* Icons slotted next to the heading inherit the title's text color
       and size to match the title's line-height. */
    ::slotted([slot='heading-icon']) {
      width: 2.625rem;
      height: 2.625rem;
      flex-shrink: 0;
      color: #212529;
    }

    .btn-close {
      padding: 0.25rem;
      background: transparent;
      border: none;
      font-size: 1.25rem;
      line-height: 1;
      cursor: pointer;
      color: #343a40;
      transition: color 0.15s;
    }

    .btn-close:hover {
      color: #000000;
    }

    .btn-back {
      padding: 0.25rem;
      background: transparent;
      border: none;
      font-size: 1.25rem;
      line-height: 1;
      cursor: pointer;
      color: #343a40;
      transition: color 0.15s;
      margin-right: 0.5rem;
    }

    .btn-back:hover {
      color: #000000;
    }

    .back-label {
      font-size: 0.875rem;
      font-family: var(--app-viewport-heading-font, 'MedievalSharp', Georgia, serif);
      vertical-align: middle;
    }

    .modal-body {
      padding: 1.25rem;
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
    }

    :host([no-padding]) .modal-body {
      padding: 0;
    }
  `;

  constructor() {
    super();
    this.heading = '';
    this.noPadding = false;
    /** @type {string|null} */
    this.backHref = null;
    this.backLabel = '';
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  async connectedCallback() {
    await super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  /** @param {KeyboardEvent} e */
  _onKeyDown(e) {
    if (e.key === 'Escape') {
      this._handleClose();
    }
  }

  _handleClose() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
  }

  _handleBack() {
    if (this.backHref) {
      router.navigate(this.backHref);
    }
  }

  render() {
    return html`
      <div class="modal-backdrop" @click="${this._handleClose}">
        <div class="modal-panel" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
          <div class="modal-header">
            <div class="header-left">
              ${
                this.backHref
                  ? html`
                    <button type="button" class="btn-back" @click="${this._handleBack}">
                      &larr; <span class="back-label">${this.backLabel}</span>
                    </button>
                  `
                  : ''
              }
            </div>
            <h2 class="modal-title">
              <slot name="heading-icon"></slot>
              ${this.heading}
            </h2>
            <div class="header-right">
              <button type="button" class="btn-close" @click="${this._handleClose}">
                <i class="bi bi-x-circle-fill"></i>
              </button>
            </div>
          </div>
          <div class="modal-body">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('app-viewport', AppViewport);
